import { useCallback, useEffect, useRef, useState } from "react";

// useState backed by sessionStorage, so a value survives navigating to a detail
// page and pressing Back (but resets when the tab/session closes).
export function usePersistedState(key, initial) {
  const [value, setValue] = useState(() => {
    try {
      const raw = sessionStorage.getItem(key);
      return raw !== null ? JSON.parse(raw) : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* ignore quota / serialization errors */
    }
  }, [key, value]);
  return [value, setValue];
}

// Remembers the window scroll position for a page and restores it once `ready`
// (i.e. the list data has rendered) after the user returns from a detail page.
export function useScrollRestore(key, ready) {
  const skey = `scroll:${key}`;
  const restored = useRef(false);

  useEffect(() => {
    let t = null;
    const onScroll = () => {
      if (t) return;
      t = setTimeout(() => {
        t = null;
        try { sessionStorage.setItem(skey, String(window.scrollY)); } catch { /* ignore */ }
      }, 150);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); if (t) clearTimeout(t); };
  }, [skey]);

  useEffect(() => {
    if (restored.current || !ready) return;
    restored.current = true;
    const y = parseInt(sessionStorage.getItem(skey) || "0", 10);
    if (y > 0) setTimeout(() => window.scrollTo(0, y), 60);
  }, [skey, ready]);

  return useCallback(() => {
    try { sessionStorage.setItem(skey, String(window.scrollY)); } catch { /* ignore */ }
  }, [skey]);
}
